import qs from 'qs';

import { connect } from './redux-websocket';
import { prefixedActionType } from './redux-websocket/actions';
import { WEBSOCKET_CONNECT } from './redux-websocket/actionTypes';

export interface WsConnectUrlConfig {
  /** Default WS URL from env config. */
  defaultUrl: string;
  /** sessionStorage key holding a URL override. */
  ssKey: string;
  /** Query-string key holding a URL override (`?<qsKey>=<url>`, `0` clears). */
  qsKey: string;
  /** Overrides are honored only when this equals `'1'` (env flag). */
  allowOverride: string | undefined;
}

/**
 * Creates a channel's `connect` action creator with the shared URL-override
 * resolution: a query-string override (persisted to sessionStorage, `0`
 * clears it) wins over a stored sessionStorage override, which wins over the
 * default env URL. Overrides apply only when `allowOverride === '1'`.
 *
 * The action's `type` is the literal `` `${prefix}::CONNECT` `` (plain `connect` widens it
 * to `` `${string}::CONNECT` ``), so for a channel with a literal prefix a
 * `Dispatch<ReturnType<typeof wsXConnect>>` accepts only that channel's connect action.
 * Both build the type with `prefixedActionType`, so the runtime value cannot drift.
 *
 * @example
 * export const wsAuthConnect = createWsConnect(prefix, {
 *   defaultUrl: AUTH_WS_URL,
 *   ssKey: AUTH_WS_SS_KEY,
 *   qsKey: AUTH_WS_QS_KEY,
 *   allowOverride: ALLOW_WS_OVERRIDE,
 * });
 */
export const createWsConnect =
  <P extends string>(prefix: P, config: WsConnectUrlConfig) =>
  () => {
    const { defaultUrl, ssKey, qsKey, allowOverride } = config;
    const type = prefixedActionType(prefix, WEBSOCKET_CONNECT);
    const connectTo = (url: string) => ({ ...connect(url, prefix), type });

    const query = qs.parse(document.location.search, { ignoreQueryPrefix: true });
    const rawVal = query[qsKey];
    const qsVal = typeof rawVal === 'string' ? rawVal : undefined;

    if (qsVal && allowOverride === '1') {
      if (qsVal !== '0') {
        sessionStorage.setItem(ssKey, qsVal);
        return connectTo(qsVal);
      }
      sessionStorage.removeItem(ssKey);
    }

    const storedWs = sessionStorage.getItem(ssKey);
    if (storedWs && allowOverride === '1') {
      return connectTo(storedWs);
    }

    return connectTo(defaultUrl);
  };
